import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CATEGORIES, categoryLabel } from "@/lib/constants";
import { toast } from "sonner";
import { Tag, Trash2, Save, Loader2, Image as ImageIcon } from "lucide-react";

const BUILTIN = new Set(CATEGORIES.map((c) => c.value));
const COLORS = [
  { value: "slate", cls: "bg-slate-500" },
  { value: "acid", cls: "bg-acid" },
  { value: "cyan", cls: "bg-cyan" },
  { value: "magenta", cls: "bg-magenta" },
  { value: "gold", cls: "bg-gold" },
  { value: "fuchsia", cls: "bg-fuchsia-500" },
  { value: "lime", cls: "bg-lime-500" },
  { value: "sky", cls: "bg-sky-500" },
  { value: "orange", cls: "bg-orange-500" },
  { value: "red", cls: "bg-red-500" },
];

const inputCls = "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid";

export default function CategoryManager() {
  const [customCats, setCustomCats] = useState([]);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState("");

  const load = async () => {
    const [catsRes, metaRes] = await Promise.all([
      api.get("/posters/categories"),
      api.get("/categories/meta"),
    ]);
    setCustomCats(catsRes.data.filter((c) => !BUILTIN.has(c)));
    const m = {};
    metaRes.data.forEach((d) => { m[d.slug] = d; });
    setMeta(m);
  };
  useEffect(() => { load(); }, []);

  const setField = (slug, k, v) => setMeta((mm) => ({ ...mm, [slug]: { ...(mm[slug] || {}), slug, [k]: v } }));

  const save = async (slug) => {
    setSaving(slug);
    const m = meta[slug] || { slug };
    try {
      await api.put("/categories/meta", {
        slug, label: m.label || "", color: m.color || "slate", cover_url: m.cover_url || "",
      });
      toast.success("Category saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(""); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? Its posters move to Eclectic.`)) return;
    await api.delete(`/categories/meta/${slug}`);
    toast.success("Category deleted");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Organize</div>
        <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">CATEGORIES</h1>
        <p className="font-sans text-sm text-muted-gray mt-3 max-w-lg">
          Rename, recolor, add cover art, or delete the custom categories you created. Built-in types can't be edited.
        </p>
      </div>

      {customCats.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center font-mono text-sm text-muted-gray" data-testid="no-custom-cats">
          You haven't created any custom categories yet. Add one from the "+ Add custom category…" option when adding a poster.
        </div>
      ) : (
        <div className="space-y-4" data-testid="category-manager-list">
          {customCats.map((slug) => {
            const m = meta[slug] || {};
            return (
              <div key={slug} className="bg-surface border border-stroke p-5" data-testid={`category-row-${slug}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="text-acid" size={18} />
                  <span className="font-mono text-xs uppercase tracking-widest text-muted-gray">{slug}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block">Display label</label>
                    <input data-testid={`cat-label-${slug}`} className={inputCls} placeholder={categoryLabel(slug)}
                      value={m.label || ""} onChange={(e) => setField(slug, "label", e.target.value)} />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block flex items-center gap-1"><ImageIcon size={11} /> Cover image URL</label>
                    <input data-testid={`cat-cover-${slug}`} className={inputCls} placeholder="https://..."
                      value={m.cover_url || ""} onChange={(e) => setField(slug, "cover_url", e.target.value)} />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-2 block">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLORS.map((c) => (
                      <button key={c.value} onClick={() => setField(slug, "color", c.value)}
                        data-testid={`cat-color-${slug}-${c.value}`}
                        className={`w-7 h-7 ${c.cls} ${(m.color || "slate") === c.value ? "ring-2 ring-offset-2 ring-offset-surface ring-paper" : ""}`} />
                    ))}
                  </div>
                </div>
                <div className="mt-5 flex gap-3">
                  <button onClick={() => save(slug)} disabled={saving === slug} data-testid={`cat-save-${slug}`}
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-2.5 hover:bg-glow disabled:opacity-50">
                    {saving === slug ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                  </button>
                  <button onClick={() => remove(slug)} data-testid={`cat-delete-${slug}`}
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider border border-magenta/50 text-magenta px-4 py-2.5 hover:bg-magenta hover:text-white">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
